import { P2GBox } from '@parcel2go/core-ui';
import { P2GButton } from '@parcel2go/core-ui';
import { P2GGrid } from '@parcel2go/core-ui';
import { P2GTextField } from '@parcel2go/core-ui';
import { P2GText } from '@parcel2go/core-ui';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { Customer } from '~/lib/api/smartsend/types';
import ValidateCustomerAccountDetails from './ValidateCustomerAccountDetails';
import { CustomerType } from '../quotes/utils/CustomerType';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { useTaxName } from '@parcel2go/npm-p2g-app';
import { CustomerTaxDetails } from '../CustomerTaxDetails';
import useDefaultCustoms from '~/apps/saved-addresses/use-default-customs';

interface props {
  customer?: Customer;
  onSave: (item: Customer) => void;
  loading: ReactNode;
  buttonText?: string;
  customerType: CustomerType;
}

export const CustomerAccountDetails: React.FC<props> = (props) => {
  const [address, setAddress] = useState<Customer>({ ...props.customer });
  const isValid = useMemo<boolean>(() => ValidateCustomerAccountDetails(address), [address]);
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const defaultCustoms = useDefaultCustoms(address.CountryIsoCode);
  const taxName = useTaxName();

  const setDetails = useCallback(
    (field: string, val: string) => {
      const i = { ...address };
      i[field] = val;
      setAddress(i);
    },
    [address]
  );

  const validation = useMemo(
    () =>
      defaultCustoms.map((x) => ({
        key: x.key,
        name: x.key,
        required: false,
        label: x.label,
        value: address.Meta[x.key]?.Value ?? address[x.key],
        pattern: x.validation
      })),
    [defaultCustoms, address]
  );

  if (props.loading) {
    return <>{props.loading}</>;
  }

  return (
    <P2GBox>
      <P2GGrid container spacing={2} alignItems="center">
        <P2GGrid item xs={12}>
          <P2GTextField
            fullWidth
            value={address.Name}
            required
            onChange={(v) => setDetails('Name', v.currentTarget.value)}
            label={t('FULL_NAME_INPUT_LABEL', { defaultValue: 'Full Name' })}
            id="Name"
          />
        </P2GGrid>
        <P2GGrid item xs={12}>
          <P2GTextField
            fullWidth
            type="email"
            required
            value={address.Email}
            onChange={(v) => setDetails('Email', v.currentTarget.value)}
            label={t('EMAIL_ADDRESS_INPUT_LABEL', { defaultValue: 'Email Address' })}
            id="Email"
          />
        </P2GGrid>
        <P2GGrid item xs={12}>
          <P2GTextField
            fullWidth
            type="tel"
            required
            value={address.Phone}
            onChange={(v) => setDetails('Phone', v.currentTarget.value)}
            label={t('PHONE_NUMBER_INPUT_LABEL', { defaultValue: 'Phone Number' })}
            id="Phone"
          />
        </P2GGrid>
        {validation.length > 0 && (
          <>
            <P2GGrid item xs={12}>
              <P2GText variant="body2">
                {t(`TAX_REGISTERED_CUSTOMER_INFO`, {
                  defaultValue: `If you’re {{taxName}} registered enter your number below`,
                  taxName: taxName
                })}
              </P2GText>
            </P2GGrid>
            <CustomerTaxDetails
              customer={address}
              onUpdate={setAddress}
              type="Sender"
              validation={validation}
              includeTitle={false}
              wrapper={(x) => (
                <P2GGrid item xs={12}>
                  {x}
                </P2GGrid>
              )}
            />
          </>
        )}
      </P2GGrid>
      <P2GBox mt={3}>
        <P2GButton variant="primary" size="large" disabled={!isValid} onClick={() => props.onSave(address)}>
          {props.buttonText || t('SAVE_BUTTON', { defaultValue: 'Save' })}
        </P2GButton>
      </P2GBox>
    </P2GBox>
  );
};
